import { createFailedToast } from '@utils/notificationToast.util'
import type { NavigateFunction } from 'react-router-dom'
import {
  ICommunicationHistory,
  CommunicationItemType,
  CommunicationDirection,
} from '@dataTypes/communicationHistory.type'
import { buildPatientProfilePreviousPage } from '@utils/patientProfileNavigation.util'

export type PatientProfileMessagePreviousPage = {
  path: string
  backLabel: string
  profileTab?: 'profile' | 'communications'
}

export type MessageCenterDeepLinkState = {
  tab: 'chat' | 'email' | 'fax' | 'calls'
  previousPage: PatientProfileMessagePreviousPage
  conversationId?: string
  chatMessageId?: string
  emailId?: string
  faxId?: string
  faxFolder?: 'inbox' | 'sent'
  callId?: string
}

const COMMUNICATION_TAB_MAP: Record<
  CommunicationItemType,
  MessageCenterDeepLinkState['tab']
> = {
  SMS: 'chat',
  Email: 'email',
  Fax: 'fax',
  Calls: 'calls',
}

export function buildPatientProfileMessagePreviousPage(
  patientId: string,
  profileTab: PatientProfileMessagePreviousPage['profileTab'] = 'profile',
): PatientProfileMessagePreviousPage {
  return {
    ...buildPatientProfilePreviousPage(patientId, profileTab),
    profileTab,
  }
}

export function buildMessageCenterStateFromCommunication(
  item: ICommunicationHistory,
  previousPage: PatientProfileMessagePreviousPage,
): MessageCenterDeepLinkState {
  const state: MessageCenterDeepLinkState = {
    tab: COMMUNICATION_TAB_MAP[item.type],
    previousPage,
  }

  if (!item.id) return state

  switch (item.type) {
    case CommunicationItemType.SMS:
      if (item.conversationId) {
        state.conversationId = item.conversationId
      }
      state.chatMessageId = item.id
      break
    case CommunicationItemType.Email:
      state.emailId = item.id
      break
    case CommunicationItemType.Fax:
      state.faxId = item.id
      state.faxFolder =
        item.direction === CommunicationDirection.Outbound ? 'sent' : 'inbox'
      break
    case CommunicationItemType.Calls:
      state.callId = item.id
      break
    default:
      break
  }

  return state
}

export function navigateToMessageFromCommunicationHistory(
  navigate: NavigateFunction,
  item: ICommunicationHistory,
  previousPage: PatientProfileMessagePreviousPage,
): void {
  navigate('/message', {
    state: buildMessageCenterStateFromCommunication(item, previousPage),
  })
}

type ShowToastFn = (toast: ReturnType<typeof createFailedToast>) => void

export function showCommunicationLogNotFoundToast(
  showToast: ShowToastFn,
  channelLabel: string,
): void {
  showToast(
    createFailedToast(
      'Could not open communication',
      `Could not open this ${channelLabel} in Message Center. It may have been deleted or is no longer available.`,
    ),
  )
}
